import {LOCATION_FIELDS} from '../config/locationFields'
import {
  validatePhoneNumber,
  validatePostalCode,
} from '../../../../utils/address-validation-util'

export function getVisibleFields(fieldConfigMap) {
  return LOCATION_FIELDS.filter(field => {
    const config = fieldConfigMap[field.key]

    return config?.visible !== false
  })
}

export function validateLocation(values, fieldConfigMap) {
  const errors = {}

  const visibleFields = getVisibleFields(fieldConfigMap)
  const countryCode = values.country || 'US'

  visibleFields.forEach(field => {
    const value = values[field.key]

    const fieldValidators = field.validators || []

    // normalize value
    const trimmedValue = typeof value === 'string' ? value.trim() : value

    // REQUIRED
    if (fieldValidators.includes('required')) {
      const isEmpty = value === undefined || value === null || value === ''

      if (isEmpty) {
        errors[field.key] = `${field.label} is required`
        return
      }
    }

    // EMAIL
    if (fieldValidators.includes('email') && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

      if (!emailRegex.test(value)) {
        errors[field.key] = 'Invalid email address'
      }
    }

    // PHONE — country-aware via google-libphonenumber
    if (fieldValidators.includes('phone') && value) {
      if (!validatePhoneNumber(countryCode, value)) {
        errors[field.key] = 'Invalid phone number'
      }
    }

    // POSTAL CODE — country-aware regex
    if (fieldValidators.includes('postalCode') && value) {
      if (!validatePostalCode(countryCode, value)) {
        errors[field.key] = 'Incorrect postal code format'
      }
    }

    // ALPHA NAME — letters, spaces, hyphens, apostrophes only (Recurly strips digits)
    if (fieldValidators.includes('alphaName') && trimmedValue) {
      if (!/^[a-zA-Z\s'-]+$/.test(trimmedValue)) {
        errors[field.key] =
          'Name must contain only letters, spaces, hyphens, or apostrophes'
      } else {
        const letterCount = trimmedValue.replace(/[^a-zA-Z]/g, '').length

        if (letterCount < 3) {
          errors[field.key] = `${field.label} must contain at least 3 letters`
        }
      }
    }

    // ABN — must be exactly 11 digits (Australia)
    if (fieldValidators.includes('abn') && value) {
      const digits = value.replace(/\D/g, '')
      if (digits.length !== 11) {
        errors[field.key] = 'Please enter a valid, 11-digit ABN'
      }
    }

    // NZBN — must be exactly 13 digits (New Zealand)
    if (fieldValidators.includes('nzbn') && value) {
      const digits = value.replace(/\D/g, '')
      if (digits.length !== 13) {
        errors[field.key] = 'Please enter a valid, 13-digit NZBN'
      }
    }
  })

  return errors
}

export const validateField = (account, value, fieldkey, fieldConfigMap) => {
  const errors = {}

  const visibleFields = getVisibleFields(fieldConfigMap)
  const countryCode = account.country || account.values.country || 'US'

  const field = visibleFields?.find(f => f.key === fieldkey)

  if (!field) return errors

  const fieldValidators = field.validators || []

  // normalize value
  const trimmedValue = typeof value === 'string' ? value.trim() : value

  // REQUIRED
  if (fieldValidators.includes('required')) {
    const isEmpty =
      trimmedValue === undefined || trimmedValue === null || trimmedValue === ''

    if (isEmpty) {
      errors[field.key] = `${field.label} is required`
      return errors
    }
  }

  // EMAIL
  if (fieldValidators.includes('email') && trimmedValue) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (!emailRegex.test(trimmedValue)) {
      errors[field.key] = 'Invalid email address'
    }
  }

  // PHONE — country-aware via google-libphonenumber
  if (fieldValidators.includes('phone') && trimmedValue) {
    if (!validatePhoneNumber(countryCode, trimmedValue)) {
      errors[field.key] = 'Invalid phone number'
    }
  }

  // POSTAL CODE — country-aware regex
  if (fieldValidators.includes('postalCode') && trimmedValue) {
    if (!validatePostalCode(countryCode, trimmedValue)) {
      errors[field.key] = 'Incorrect postal code format'
    }
  }

  // ALPHA NAME — letters, spaces, hyphens, apostrophes only (Recurly strips digits)
  if (fieldValidators.includes('alphaName') && trimmedValue) {
    if (!/^[a-zA-Z\s'-]+$/.test(trimmedValue)) {
      errors[field.key] =
        'Name must contain only letters, spaces, hyphens, or apostrophes'
    } else {
      const letterCount = trimmedValue.replace(/[^a-zA-Z]/g, '').length

      if (letterCount < 3) {
        errors[field.key] = `${field.label} must contain at least 3 letters`
      }
    }
  }

  // ABN — must be exactly 11 digits (Australia)
  if (fieldValidators.includes('abn') && trimmedValue) {
    const digits = trimmedValue.replace(/\D/g, '')
    if (digits.length !== 11) {
      errors[field.key] = 'Please enter a valid, 11-digit ABN'
    }
  }

  // NZBN — must be exactly 13 digits (New Zealand)
  if (fieldValidators.includes('nzbn') && trimmedValue) {
    const digits = trimmedValue.replace(/\D/g, '')
    if (digits.length !== 13) {
      errors[field.key] = 'Please enter a valid, 13-digit NZBN'
    }
  }

  return errors
}

const ERROR_FIELD_MAP = [
  {
    field: 'state',
    keywords: ['the state/province'],
  },
  {
    field: 'city',
    keywords: ['the city'],
  },
  {
    field: 'postCode',
    keywords: ['the postal', 'the postal code'],
  },
]

export const getFieldFromErrorMessage = message => {
  const normalizedMessage = message.toLowerCase()
  const field =
    ERROR_FIELD_MAP.find(({keywords}) =>
      keywords.some(keyword => normalizedMessage.includes(keyword)),
    )?.field || null
  return field
}
